import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  List,
  Modal,
  Space,
  Table,
  Tabs,
  Typography,
  message,
} from "antd";
import styled from "styled-components";
import { getPaymentTermsTemplates } from "@/features/providers/services/paymentTermsService";
import {
  getCurrentTerms,
  publishTerms,
} from "@/features/termsManagement/services/termsService";
import {
  DEFAULT_QUOTE_LINK_PRIVACY_POLICY,
  DEFAULT_QUOTE_LINK_TERMS,
} from "@/features/termsManagement/utils/quoteLinkTermsDefaults";

const { Title, Text } = Typography;

const Container = styled.div`
  padding: 24px;
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InlineRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const TermsManagementView = () => {
  const [quoteForm] = Form.useForm();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingCurrentTerms, setLoadingCurrentTerms] = useState(true);
  const [savingTerms, setSavingTerms] = useState(false);

  const quoteLinkDefaults = useMemo(
    () => ({
      callNumber: import.meta.env.VITE_CALL_NUMBER || "0676744632",
      contactEmail: "info@bubakii.co.za",
      terms: DEFAULT_QUOTE_LINK_TERMS,
      privacyPolicy: DEFAULT_QUOTE_LINK_PRIVACY_POLICY,
    }),
    []
  );

  // Load current published terms from backend
  useEffect(() => {
    const loadCurrentTerms = async () => {
      setLoadingCurrentTerms(true);
      try {
        const result = await getCurrentTerms();
        if (result.success && result.data) {
          // Load published terms from backend
          quoteForm.setFieldsValue({
            callNumber: result.data.callNumber || quoteLinkDefaults.callNumber,
            contactEmail: result.data.contactEmail || quoteLinkDefaults.contactEmail,
            terms: (result.data.terms || []).map((t) => ({ text: t })),
            privacyPolicy: (result.data.privacyPolicy || []).map((t) => ({ text: t })),
          });
        } else {
          // Fallback to defaults if no published terms
          quoteForm.setFieldsValue({
            callNumber: quoteLinkDefaults.callNumber,
            contactEmail: quoteLinkDefaults.contactEmail,
            terms: quoteLinkDefaults.terms.map((t) => ({ text: t })),
            privacyPolicy: quoteLinkDefaults.privacyPolicy.map((t) => ({ text: t })),
          });
        }
      } catch (error) {
        // On error, use defaults
        quoteForm.setFieldsValue({
          callNumber: quoteLinkDefaults.callNumber,
          contactEmail: quoteLinkDefaults.contactEmail,
          terms: quoteLinkDefaults.terms.map((t) => ({ text: t })),
          privacyPolicy: quoteLinkDefaults.privacyPolicy.map((t) => ({ text: t })),
        });
      } finally {
        setLoadingCurrentTerms(false);
      }
    };

    loadCurrentTerms();
  }, [quoteForm, quoteLinkDefaults]);

  const fetchTemplates = async () => {
    setTemplatesError(null);
    setTemplatesLoading(true);
    try {
      const result = await getPaymentTermsTemplates();
      if (!result?.success) {
        throw new Error(result?.error || "Failed to fetch templates");
      }
      const data = Array.isArray(result.data) ? result.data : result.data?.templates || [];
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      setTemplatesError(err?.message || "Failed to fetch templates");
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleSaveQuoteLinkTerms = async () => {
    try {
      setSavingTerms(true);
      const values = await quoteForm.validateFields();
      const payload = {
        callNumber: values.callNumber,
        contactEmail: values.contactEmail,
        terms: (values.terms || []).map((t) => t?.text).filter(Boolean),
        privacyPolicy: (values.privacyPolicy || [])
          .map((t) => t?.text)
          .filter(Boolean),
      };

      const result = await publishTerms(payload);
      if (result.success) {
        message.success("Terms published successfully! All customers will see the updated terms.");
      } else {
        message.error(result.error || "Failed to publish terms");
      }
    } catch (error) {
      message.error(error.message || "Failed to save terms");
    } finally {
      setSavingTerms(false);
    }
  };

  const handleResetQuoteLinkTerms = () => {
    quoteForm.setFieldsValue({
      callNumber: quoteLinkDefaults.callNumber,
      contactEmail: quoteLinkDefaults.contactEmail,
      terms: quoteLinkDefaults.terms.map((t) => ({ text: t })),
      privacyPolicy: quoteLinkDefaults.privacyPolicy.map((t) => ({ text: t })),
    });
    message.info("Reset to defaults (not saved yet)");
  };

  const previewData = useMemo(() => {
    const values = quoteForm.getFieldsValue();
    return {
      callNumber: values.callNumber,
      contactEmail: values.contactEmail,
      terms: (values.terms || []).map((t) => t?.text).filter(Boolean),
      privacyPolicy: (values.privacyPolicy || []).map((t) => t?.text).filter(Boolean),
    };
  }, [quoteForm]);

  const getPayoutTriggerLabel = (trigger) => {
    const labels = {
      FULL_PAYMENT: "Full Payment Required",
      DEPOSIT_ONLY: "Deposit Only",
      COMPLETION_PLUS_DAYS: "Completion + Days Delay",
      PAYDAY_ONLY: "Monthly Payday Only (No Advance)",
      PAYDAY_WITH_ADVANCE: "Monthly Payday (Advance Allowed)",
    };
    return labels[trigger] || trigger || "—";
  };

  const templatesColumns = useMemo(
    () => [
      {
        title: "Template ID",
        dataIndex: "id",
        key: "id",
        render: (value) => value || "—",
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Payout trigger",
        dataIndex: "payoutTrigger",
        key: "payoutTrigger",
        render: (value) => getPayoutTriggerLabel(value),
      },
      {
        title: "Retention %",
        dataIndex: "retentionPercent",
        key: "retentionPercent",
        render: (value) => (value !== null && value !== undefined ? `${value}%` : "—"),
      },
      {
        title: "Dispute window (days)",
        dataIndex: "disputeWindowDays",
        key: "disputeWindowDays",
      },
      {
        title: "Payout delay (days)",
        dataIndex: "payoutDelayDays",
        key: "payoutDelayDays",
        render: (value) => (value !== null && value !== undefined ? value : "—"),
      },
    ],
    []
  );

  return (
    <Container>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <HeaderBlock>
          <Title level={2}>Terms Management</Title>
          <Text type="secondary">
            Manage customer-facing Terms & Conditions and operational payment terms templates.
          </Text>
        </HeaderBlock>

        <Alert
          type="info"
          showIcon
          message="What this page controls"
          description="Customer quote-link Terms & Conditions can be edited and published here. Published terms are stored in the database and displayed to customers. Provider Payment Terms can be viewed and edited for each provider."
        />

        <Tabs
          defaultActiveKey="quoteLink"
          items={[
            {
              key: "quoteLink",
              label: "Quote Link T&Cs",
              children: (
                <Card
                  title="Customer-facing Terms & Conditions (Quote Link)"
                  extra={
                    <InlineRow>
                      <Button onClick={handleResetQuoteLinkTerms} disabled={loadingCurrentTerms || savingTerms}>
                        Reset
                      </Button>
                      <Button onClick={() => setPreviewOpen(true)} disabled={loadingCurrentTerms || savingTerms}>
                        Preview
                      </Button>
                      <Button type="primary" onClick={handleSaveQuoteLinkTerms} loading={savingTerms} disabled={loadingCurrentTerms}>
                        Publish
                      </Button>
                    </InlineRow>
                  }
                >
                  <SectionBlock>
                    {loadingCurrentTerms && (
                      <Alert
                        type="info"
                        showIcon
                        message="Loading current terms..."
                        description="Fetching published terms from the server."
                      />
                    )}

                    <Form form={quoteForm} layout="vertical">
                      <InlineRow>
                        <Form.Item
                          label="Call number"
                          name="callNumber"
                          style={{ minWidth: 280, flex: 1 }}
                          rules={[{ required: true, message: "Call number is required" }]}
                        >
                          <Input placeholder="0676744632" />
                        </Form.Item>
                        <Form.Item
                          label="Contact email"
                          name="contactEmail"
                          style={{ minWidth: 280, flex: 1 }}
                          rules={[
                            { required: true, message: "Contact email is required" },
                            { type: "email", message: "Enter a valid email" },
                          ]}
                        >
                          <Input placeholder="info@bubakii.co.za" />
                        </Form.Item>
                      </InlineRow>

                      <Card size="small" title="Service Terms">
                        <Form.List name="terms">
                          {(fields, { add, remove }) => (
                            <Space direction="vertical" style={{ width: "100%" }}>
                              {fields.map((field) => (
                                <InlineRow key={field.key}>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, "text"]}
                                    style={{ flex: 1, minWidth: 320, marginBottom: 0 }}
                                    rules={[
                                      { required: true, message: "Term text is required" },
                                    ]}
                                  >
                                    <Input placeholder="Enter a term..." />
                                  </Form.Item>
                                  <Button danger onClick={() => remove(field.name)}>
                                    Remove
                                  </Button>
                                </InlineRow>
                              ))}
                              <Button onClick={() => add({ text: "" })}>Add term</Button>
                            </Space>
                          )}
                        </Form.List>
                      </Card>

                      <Card size="small" title="Privacy Policy">
                        <Form.List name="privacyPolicy">
                          {(fields, { add, remove }) => (
                            <Space direction="vertical" style={{ width: "100%" }}>
                              {fields.map((field) => (
                                <InlineRow key={field.key}>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, "text"]}
                                    style={{ flex: 1, minWidth: 320, marginBottom: 0 }}
                                    rules={[
                                      { required: true, message: "Policy text is required" },
                                    ]}
                                  >
                                    <Input placeholder="Enter a privacy policy item..." />
                                  </Form.Item>
                                  <Button danger onClick={() => remove(field.name)}>
                                    Remove
                                  </Button>
                                </InlineRow>
                              ))}
                              <Button onClick={() => add({ text: "" })}>Add policy item</Button>
                            </Space>
                          )}
                        </Form.List>
                      </Card>
                    </Form>
                  </SectionBlock>
                </Card>
              ),
            },
            {
              key: "paymentTerms",
              label: "Provider Payment Terms Templates",
              children: (
                <Card
                  title="Provider Payment Terms Templates"
                  extra={
                    <InlineRow>
                      <Button onClick={fetchTemplates} loading={templatesLoading}>
                        Refresh
                      </Button>
                    </InlineRow>
                  }
                >
                  <SectionBlock>
                    <Alert
                      type="info"
                      showIcon
                      message="Payment Terms Templates"
                      description="These are read-only reference templates that can be applied to providers. To edit provider-specific payment terms, go to the provider management page."
                      style={{ marginBottom: 16 }}
                    />
                    <Text type="secondary">
                      These templates come from the backend and are used to configure provider payout terms. Templates can be applied to providers but cannot be edited directly.
                    </Text>

                    {templatesError && (
                      <Alert type="error" showIcon message="Failed to load templates" description={templatesError} />
                    )}

                    <Table
                      rowKey={(row) => row.id || row.templateId || row.name || JSON.stringify(row)}
                      columns={templatesColumns}
                      dataSource={templates}
                      loading={templatesLoading}
                      pagination={{ pageSize: 10 }}
                    />
                  </SectionBlock>
                </Card>
              ),
            },
            {
              key: "audit",
              label: "Versioning & Acceptance (next)",
              children: (
                <Card title="What we should add next (recommended)">
                  <List
                    bordered
                    dataSource={[
                      "Versioning: each change produces a new version with effective date/time",
                      "Acceptance tracking: record who accepted which version (quote token, customer, timestamp)",
                      "Preview + publish workflow: draft → review → publish",
                      "Audit log: who changed what, and why",
                      "Scoped overrides: per customer / per partner / per service type",
                      "Localization: different terms by locale/language",
                      "Export/import: JSON export for legal review and backups",
                    ]}
                    renderItem={(item) => <List.Item>{item}</List.Item>}
                  />
                </Card>
              ),
            },
          ]}
        />
      </Space>

      <Modal
        title="Preview: Quote Link Terms & Conditions"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={<Button onClick={() => setPreviewOpen(false)}>Close</Button>}
        width={800}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Card size="small" title="Contact">
            <Space direction="vertical">
              <Text>
                <Text strong>Call:</Text> {previewData.callNumber || "—"}
              </Text>
              <Text>
                <Text strong>Email:</Text> {previewData.contactEmail || "—"}
              </Text>
            </Space>
          </Card>
          <Card size="small" title="Service Terms">
            <List
              dataSource={previewData.terms}
              renderItem={(item) => <List.Item>{item}</List.Item>}
            />
          </Card>
          <Card size="small" title="Privacy Policy">
            <List
              dataSource={previewData.privacyPolicy}
              renderItem={(item) => <List.Item>{item}</List.Item>}
            />
          </Card>
        </Space>
      </Modal>

    </Container>
  );
};

export default TermsManagementView;
